import { useCreateOrUpdateBibliographyInBibliography } from "@/api/related-fields";
import { LinkableType } from "@/components/LinksManagement";
import { FormLayoutElement } from "@/config/forms/general";
import { BibliographyInBibliography } from "@/lib/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import LinksDrawerLayout from "./LinksDrawerLayout";

/**
 * Drawer d'édition du lien complexe Biblio ↔ Biblio.
 * ⚠️ Champs PLACEHOLDER — à finaliser selon le retour client. Le formulaire est
 * piloté par la config `fields` (donc l'ajout de champs se fait côté config).
 * `queryId` = fiche courante (bibliographyA) ; `data.bibliography` = fiche liée
 * (bibliographyB).
 */
const BibliographyInBibliographyDrawer = ({
  fields,
  data,
  queryId,
  type,
  open,
  onOpenChange,
}: {
  fields: FormLayoutElement[] | undefined;
  data: BibliographyInBibliography;
  queryId: string;
  type: LinkableType;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) => {
  const schema = z.object({
    bibliographieDansBibliographieDescriptionPage: z.string().optional(),
    bibliographieDansBibliographieDescriptionReproduitPage: z
      .string()
      .optional(),
    bibliographieDansBibliographieDescriptionCitePage: z.string().optional(),
    bibliographieDansBibliographieDescriptionListePage: z.string().optional(),
    bibliographieDansBibliographieDescriptionNonPagine: z.boolean().optional(),
    bibliographieDansBibliographieDescriptionReproductionCouverture: z
      .boolean()
      .optional(),
    bibliographieDansBibliographieDescriptionReproductionCouleur: z
      .boolean()
      .optional(),
    bibliographieDansBibliographieDescriptionTextePersonnalise: z
      .string()
      .optional(),
    bibliography: z.object({
      bibliographieDescriptionNumeroBibliographie: z.string().optional(),
      bibliographieDescriptionResumeLong: z.string().optional(),
    }),
  });

  const { mutate: createOrUpdateBibliographyInBibliography } =
    useCreateOrUpdateBibliographyInBibliography(queryId, type);

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      bibliography: data.bibliography,
      bibliographieDansBibliographieDescriptionPage:
        data.bibliographieDansBibliographieDescriptionPage,
      bibliographieDansBibliographieDescriptionReproduitPage:
        data.bibliographieDansBibliographieDescriptionReproduitPage,
      bibliographieDansBibliographieDescriptionCitePage:
        data.bibliographieDansBibliographieDescriptionCitePage,
      bibliographieDansBibliographieDescriptionListePage:
        data.bibliographieDansBibliographieDescriptionListePage,
      bibliographieDansBibliographieDescriptionNonPagine:
        data.bibliographieDansBibliographieDescriptionNonPagine,
      bibliographieDansBibliographieDescriptionReproductionCouverture:
        data.bibliographieDansBibliographieDescriptionReproductionCouverture,
      bibliographieDansBibliographieDescriptionReproductionCouleur:
        data.bibliographieDansBibliographieDescriptionReproductionCouleur,
      bibliographieDansBibliographieDescriptionTextePersonnalise:
        data.bibliographieDansBibliographieDescriptionTextePersonnalise,
    },
  });

  const onSubmit = (formData: z.infer<typeof schema>) => {
    createOrUpdateBibliographyInBibliography({
      id: data.id,
      // bibliographyA = fiche courante, bibliographyB = fiche liée.
      bibliographyAId: queryId,
      bibliographyBId: data.bibliography.id,
      ...formData,
    });
  };

  return (
    <LinksDrawerLayout
      form={form}
      fields={fields ?? []}
      recordId={data.id}
      onSubmit={onSubmit}
      open={open}
      onOpenChange={onOpenChange}
    />
  );
};

export default BibliographyInBibliographyDrawer;
